import React, { useState } from 'react';
import styled from 'styled-components';
import { FiMinimize2 } from 'react-icons/fi';
import { Signal } from '@preact/signals-react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { useSignals } from '@preact/signals-react/runtime';
import Portal from '@/components/portal';
import { DEVICE_NAV_PARENT_ID } from '@/features/editor/preview-navigation/index';
import { EditorConfig } from '@/features/ai-agents/chat/editor-config';
import { useProxyActions } from '@/features/editor/widgets/shared/ai-editing-layout/hooks/use-proxy-actions';
import { VSpace } from '@/components/spacing';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { maybe } from '@/features/details/utils';

interface ShowAddedElementsButtonProps {
  isVisible: boolean;
  editorConfig: Signal<EditorConfig>;
}

export function ElementConfig({
  isVisible,
  editorConfig,
}: ShowAddedElementsButtonProps) {
  useSignals();
  const name = maybe(
    () => useAiComponentContext((ctx) => ctx?.newChange?.value?.hint),
    ``,
  );
  const rerender = maybe(() => useAiComponentContext((ctx) => ctx?.rerender));
  const [open, setOpen] = useState(false);
  const { runState, runAction } = useProxyActions();

  if (!isVisible) return null;

  const config = editorConfig?.value;
  if (!config) return null;
  const actions = config?.actions || {};
  const states = config?.states || {};
  const hasActions = Object.keys(actions).length > 0;
  const hasStates = Object.keys(states).length > 0;

  const onStateClicked = (key: string) => {
    runState(key);
    setOpen(false);
  };

  const onActionClicked = (key: string) => {
    runAction(key);
    setOpen(false);
  };

  const onInitial = () => {
    rerender?.();
    setOpen(false);
  };

  return (
    <Portal selector={`#${DEVICE_NAV_PARENT_ID}`}>
      <Popover
        onClickOutside={() => setOpen(false)}
        isOpen={open}
        containerStyle={{ zIndex: `29` }}
        positions={[`bottom`]}
        content={({ position, childRect, popoverRect }) => (
          <ArrowContainer
            position={position}
            childRect={childRect}
            popoverRect={popoverRect}
            arrowColor="white"
            arrowSize={10}
            arrowStyle={{ margin: `1px 1px 1px 3px` }}
          >
            <PopoverContainer>
              {hasActions && (
                <>
                  <Header>ACTIONS</Header>
                  <Divider />
                  {Object.entries(actions).map(([key, item]) => (
                    <ActionItem key={key} onClick={() => onActionClicked(key)}>
                      <ActionKey>{item.name}</ActionKey>
                      <ActionValue>{item.description}</ActionValue>
                    </ActionItem>
                  ))}
                </>
              )}

              {hasActions && <VSpace value={1.5} />}
              <Header>STATES</Header>
              <Divider />
              <StateItem onClick={onInitial}>
                <StateKey>Initial</StateKey>
                <StateValue>Return to the original state</StateValue>
              </StateItem>
              {hasStates && (
                <>
                  {Object.entries(states).map(([key, item]) => (
                    <StateItem key={key} onClick={() => onStateClicked(key)}>
                      <StateKey>{item.name}</StateKey>
                      <StateValue>{item.description}</StateValue>
                    </StateItem>
                  ))}
                </>
              )}
              {!hasActions && !hasStates && (
                <EmptyMessage>No actions or states available</EmptyMessage>
              )}
            </PopoverContainer>
          </ArrowContainer>
        )}
      >
        <ButtonWrapper>
          <Button onClick={() => setOpen(!open)}>
            <FiMinimize2 size={14} />
            {name || `Added element`}
          </Button>
        </ButtonWrapper>
      </Popover>
    </Portal>
  );
}

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;

  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const Button = styled.button`
  appearance: none;
  outline: none;
  color: #171b21;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.09);
  background-color: #fff;
  border-radius: 1.5rem;
  padding: 0.725rem 1.2rem;
  height: 3.05rem;
  font-family: 'Inter', serif;
  font-weight: 500;
  font-size: 1.2rem;
  cursor: pointer;
  white-space: nowrap;

  display: flex;
  align-items: center;
  justify-content: center;

  gap: 0.8rem;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: rgba(153, 153, 153, 0.15);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const PopoverContainer = styled.div`
  user-select: none;
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 1.6rem 0px;
  min-width: 280px;
  max-width: 400px;
  border-radius: 18px;
  border: solid 1px rgba(151, 151, 151, 0.1);
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.09),
    0 1px 4px 0 rgba(14, 12, 12, 0.08);
  max-width: 20rem;
`;

const Header = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: #9f9f9f;
  padding: 0px 1.6rem;
  margin: 0.2rem 0rem 0.6rem 0.3rem;
`;

const ActionItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1rem 1.6rem;
  cursor: pointer;
  margin-left: 0.6rem;

  &:hover {
    opacity: 0.7;
  }
`;

const ActionKey = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: #000;
`;

const ActionValue = styled.div`
  font-size: 1.1rem;
  font-weight: 400;
  color: #666;
  word-wrap: break-word;
`;

const StateItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 1rem 1.6rem;
  margin-left: 0.6rem;
  cursor: pointer;

  &:hover {
    opacity: 0.7;
  }
`;

const StateKey = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: #000;
`;

const StateValue = styled.div`
  font-size: 1.1rem;
  font-weight: 400;
  color: #666;
  word-wrap: break-word;
`;

const Divider = styled.div`
  height: 1px;
  background-color: #dcdcdc;
  opacity: 0.65;
  margin: 0.5rem 1.6rem 0.5rem 1.6rem;
`;

const EmptyMessage = styled.div`
  font-size: 1.3rem;
  font-weight: 400;
  color: #999;
  padding: 1rem 1.6rem;
  text-align: center;
`;
